"use client";
import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";

type Accent = "blue" | "orange" | "emerald";

const accentClass: Record<Accent, string> = {
  blue: "text-blue-600",
  orange: "text-orange-600",
  emerald: "text-emerald-600",
};

export interface ListPaginationFooterProps {
  page: number;
  totalPages: number;
  totalCount: number;
  /** Number of items on the current page */
  currentCount: number;
  /** Plural label, e.g. "orders", "invoices", "estimates" */
  itemLabel: string;
  accent?: Accent;
  onPrev: () => void;
  onNext: () => void;
}

/** Same footer pattern as the Orders list page. */
export const ListPaginationFooter: React.FC<ListPaginationFooterProps> = ({
  page,
  totalPages,
  totalCount,
  currentCount,
  itemLabel,
  accent = "blue",
  onPrev,
  onNext,
}) => {
  if (totalPages <= 1) return null;

  const highlight = accentClass[accent];

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white rounded-xl p-4 border-2 border-gray-200">
      <p className="text-sm font-medium text-gray-600">
        Showing <span className={`font-bold ${highlight}`}>{currentCount}</span> of{" "}
        <span className={`font-bold ${highlight}`}>{totalCount}</span> {itemLabel}
      </p>
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          disabled={page === 1}
          onClick={onPrev}
          className="flex items-center gap-2 disabled:opacity-50"
        >
          <ChevronLeft size={18} />
          <span className="hidden sm:inline">Previous</span>
        </Button>

        <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg">
          <span className="text-sm font-bold text-gray-700">
            {page} / {totalPages}
          </span>
        </div>

        <Button
          variant="outline"
          disabled={page === totalPages}
          onClick={onNext}
          className="flex items-center gap-2 disabled:opacity-50"
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight size={18} />
        </Button>
      </div>
    </div>
  );
};
